<template>
  <img
      :src="displayedSrc"
      :alt="`logo-${teamId}`"
  />
</template>

<script>
  import {mapGetters} from 'vuex';
  import {normalizeHex, getShirtColors} from '@/services/competitorShirts';

// Blends a '#rrggbb' colour towards black (0) or white (255) by `amount` (0..1).
const mix = (hex, target, amount) => '#' + [1, 3, 5]
  .map(i => { const c = parseInt(hex.slice(i, i + 2), 16); return Math.round(c + (target - c) * amount).toString(16).padStart(2, '0'); })
  .join('');
const colorDistance = (a, b) => Math.sqrt([1, 3, 5].reduce((sum, i) => sum + (parseInt(a.slice(i, i + 2), 16) - parseInt(b.slice(i, i + 2), 16)) ** 2, 0));

const SHIRT_PATH = 'M50 15L29 22Q20 25 16 34L6 58L29 67L37 52V134Q75 141 113 134V52L121 67L144 58L134 34Q130 25 121 22L100 15Q87 26 75 26Q63 26 50 15Z';

// Shirt drawn with the team colours: body in the primary, edge/collar/cuffs in
// the outline, side panels/piping/badge in the number colour.
// Each data URI is its own SVG document, so the ids don't need to be unique.
const buildShirtSvg = ({primary, number, outline}) => {
  const id = 'shirt';
  const outlineVisible = outline && colorDistance(outline, primary) > 40;
  // When the outline is the same as the shirt, darken it so the shape still reads.
  const edge = outlineVisible ? outline : mix(primary, 0, 0.45);
  const trim = outlineVisible ? outline : (number || edge);
  const accent = number || mix(primary, 255, 0.7);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150">`
    + `<defs>`
    + `<linearGradient id="${id}g" x1="0" y1="0" x2="1" y2="0">`
    + `<stop offset="0" stop-color="#000" stop-opacity=".18"/><stop offset=".22" stop-color="#000" stop-opacity="0"/>`
    + `<stop offset=".5" stop-color="#fff" stop-opacity=".12"/>`
    + `<stop offset=".78" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".18"/>`
    + `</linearGradient>`
    + `<clipPath id="${id}c"><path d="${SHIRT_PATH}"/></clipPath>`
    + `</defs>`
    + `<path d="${SHIRT_PATH}" fill="${primary}"/>`
    + `<g clip-path="url(#${id}c)">`
    // Side panels
    + `<path d="M37 70L44 70L47 140H37Z M113 70L106 70L103 140H113Z" fill="${accent}"/>`
    // Cuffs with piping
    + `<path d="M2 50L33 62L27 75L-4 63Z M148 50L117 62L123 75L154 63Z" fill="${trim}"/>`
    + `<path d="M5 53.5L31.5 64M145 53.5L118.5 64" stroke="${accent}" stroke-width="2.5"/>`
    // Fabric shading
    + `<rect width="150" height="150" fill="url(#${id}g)"/>`
    + `</g>`
    // Collar with piping
    + `<path d="M50 15Q63 30 75 30Q87 30 100 15" fill="none" stroke="${trim}" stroke-width="8" stroke-linecap="round"/>`
    + `<path d="M53 18Q64 30.5 75 30.5Q86 30.5 97 18" fill="none" stroke="${accent}" stroke-width="2"/>`
    // Badge
    + `<path d="M88 45H102V53Q102 59 95 62Q88 59 88 53Z" fill="${accent}" stroke="${trim}" stroke-width="1.5"/>`
    + `<path d="${SHIRT_PATH}" fill="none" stroke="${edge}" stroke-width="3.5" stroke-linejoin="round"/>`
    + `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

// One data URI per colour combination, shared by every instance.
const shirtCache = new Map();
const getShirtSrc = colors => {
  const key = `${colors.primary}|${colors.number}|${colors.outline}`;
  let src = shirtCache.get(key);
  if (!src) {
    src = buildShirtSvg(colors);
    shirtCache.set(key, src);
  }
  return src;
};

const COMPETITORS_PATH = process.env.VUE_APP_URL_CLOUDFRONT + '/svg/competitors/';
const LOGO_FORMATS = ["webp", "svg", "png"];

const checkImage = src => new Promise(resolve => {
  const img = new window.Image();
  img.onload = () => resolve(true);
  img.onerror = () => resolve(false);
  img.src = src;
});

// Logo lookup once per team (the same team is rendered many times): resolves
// to the logo url, or '' when the team has none.
const logoCache = new Map();
const findTeamLogo = teamId => {
  const key = String(teamId);
  if (!logoCache.has(key)) {
    logoCache.set(key, (async () => {
      for (const format of LOGO_FORMATS) {
        const src = `${COMPETITORS_PATH}${key}.${format}`;
        if (await checkImage(src)) return src;
      }
      return '';
    })());
  }
  return logoCache.get(key);
};

export default {
  name: "TeamLogo",
  props: {
    teamId: { type: [String, Number], required: true },
    height: { type: String, default: "30px" },
    width: { type: String, default: "30px" },
    // Optional shirt colours (event-detail `competitors[].shirtColor*`); without
    // them they are looked up by teamId among the loaded event-details.
    shirtColorPrimary: { type: String, default: null },
    shirtColorNumber: { type: String, default: null },
    shirtColorOutline: { type: String, default: null },
  },
  data() {
    return {
      logoSrc: "",
    }
  },
  computed: {
    ...mapGetters({
      showParticipantLogos: 'settings/getShowParticipantLogos',
    }),
    fallbackSrc() {
      // Fallback deterministico tra team-1 e team-4
      return `${COMPETITORS_PATH}team-${(Number(this.teamId) % 4) + 1}.webp`;
    },
    shirtSrc() {
      const primary = normalizeHex(this.shirtColorPrimary);
      const colors = primary
        ? { primary, number: normalizeHex(this.shirtColorNumber), outline: normalizeHex(this.shirtColorOutline) }
        : getShirtColors(this.teamId);
      return colors ? getShirtSrc(colors) : "";
    },
    displayedSrc() {
      return this.logoSrc || this.shirtSrc || this.fallbackSrc;
    },
  },
  mounted() {
    this.loadTeamLogo();
  },
  watch: {
    teamId() {
      this.loadTeamLogo();
    },
    // Reapply behavior when the platform-wide flag changes at runtime
    showParticipantLogos() {
      this.loadTeamLogo();
    },
  },
  methods: {
    async loadTeamLogo() {
      // Mostra sempre la maglia finché il logo non è caricato davvero!
      this.logoSrc = "";
      if (!this.showParticipantLogos) return;
      const teamId = this.teamId;
      const src = await findTeamLogo(teamId);
      // Il team potrebbe essere cambiato nel frattempo
      if (teamId === this.teamId && this.showParticipantLogos) this.logoSrc = src;
    },
  }
}
</script>
